import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, Download, RefreshCw } from 'lucide-react';
import html2canvas from 'html2canvas';
import { QRCodeCanvas } from 'qrcode.react';

const QUESTIONS = [
  {
    question: "難得的週末下午，你通常會想做什麼？",
    options: [
      { text: "出門流點汗，跟朋友打場球", category: "運動", type: "active" },
      { text: "找間舒適的咖啡廳或看展覽", category: "文藝", type: "chill" },
      { text: "去戶外走走，遠離城市喧囂", category: "戶外", type: "explore" },
      { text: "找一群人玩桌遊或聚餐聊天", category: "聚會", type: "social" }
    ]
  },
  {
    question: "如果要在 JoinU 上找朋友，你最看重什麼？",
    options: [
      { text: "大家的實力差不多，打起來才過癮", category: "運動", type: "active" },
      { text: "頻率對不對，能不能深度聊天", category: "文藝", type: "chill" },
      { text: "隨和好相處，不怕踩雷的旅伴", category: "旅遊", type: "explore" },
      { text: "人越多越好，氣氛熱鬧最重要", category: "聚會", type: "social" }
    ]
  },
  {
    question: "你的消費習慣偏向哪一種？",
    options: [
      { text: "平價高CP值，重點是大家開心", type: "budget" },
      { text: "願意花錢享受高品質的體驗", type: "premium" },
      { text: "AA制算清楚，自己出自己的最沒壓力", type: "fair" },
      { text: "看當天心情，該花就花不手軟", type: "flex" }
    ]
  }
];

const RESULTS = {
  active: {
    title: "熱血行動派",
    desc: "坐不住的你，最適合用汗水交朋友！",
    category: "運動",
    color: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    emoji: "🔥"
  },
  chill: {
    title: "質感品味家",
    desc: "享受生活細節，喜歡深度的交流與陪伴。",
    category: "文藝",
    color: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    emoji: "☕"
  },
  explore: {
    title: "隨性漫遊者",
    desc: "嚮往自由與大自然，說走就走的最佳旅伴！",
    category: "旅遊",
    color: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    emoji: "✈️"
  },
  social: {
    title: "派對氣氛王",
    desc: "有你在的地方就有歡笑，天生的破冰大師！",
    category: "聚會",
    color: "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
    emoji: "🎉"
  }
};

export default function Quiz() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(-1); // -1 = Start, 0-2 = Questions, 3 = Result
  const [answers, setAnswers] = useState([]);
  const resultRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleStart = () => setCurrentStep(0);

  const handleAnswer = (option) => {
    const newAnswers = [...answers, option];
    setAnswers(newAnswers);
    if (currentStep < QUESTIONS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setCurrentStep(QUESTIONS.length);
    }
  };

  const getResult = () => {
    // Simple logic: count types from Q1 & Q2
    const types = answers.map(a => a.type).filter(Boolean);
    const counts = types.reduce((acc, val) => {
      acc[val] = (acc[val] || 0) + 1;
      return acc;
    }, {});
    
    // Find max
    let maxType = 'social'; // default
    let maxCount = 0;
    Object.keys(counts).forEach(type => {
      if (counts[type] > maxCount) {
        maxCount = counts[type];
        maxType = type;
      }
    });

    return RESULTS[maxType] || RESULTS.social;
  };

  const handleDownload = async () => {
    if (!resultRef.current) return;
    setIsGenerating(true);
    try {
      const canvas = await html2canvas(resultRef.current, {
        scale: 3,
        useCORS: true,
        backgroundColor: '#ffffff'
      });
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `joinu-quiz-result.png`;
      link.click();
    } catch (error) {
      console.error('Failed to generate image', error);
      alert('產生圖片失敗，請稍後再試');
    } finally {
      setIsGenerating(false);
    }
  };

  const renderStart = () => (
    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: '80px', marginBottom: '20px' }}>🔮</div>
      <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', marginBottom: '16px' }}>
        不知道想做什麼？<br/>測測你的潛在社交人格！
      </h1>
      <p style={{ color: '#64748b', marginBottom: '40px', lineHeight: '1.6' }}>
        只需要 30 秒，找出最適合你的交友模式與活動推薦。
      </p>
      <button 
        onClick={handleStart}
        style={{ width: '100%', maxWidth: '300px', padding: '16px', background: '#4e8bca', color: 'white', border: 'none', borderRadius: '16px', fontSize: '18px', fontWeight: '800', cursor: 'pointer', boxShadow: '0 8px 20px rgba(78, 139, 202, 0.3)' }}
      >
        開始測驗
      </button>
    </div>
  );

  const renderQuestion = () => {
    const q = QUESTIONS[currentStep];
    return (
      <div style={{ padding: '20px' }}>
        <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '700', marginBottom: '16px' }}>
          第 {currentStep + 1} / {QUESTIONS.length} 題
        </div>
        <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#0f172a', marginBottom: '32px', lineHeight: '1.4' }}>
          {q.question}
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {q.options.map((opt, idx) => (
            <button
              key={idx}
              onClick={() => handleAnswer(opt)}
              style={{
                width: '100%',
                padding: '18px 20px',
                background: '#ffffff',
                border: '2px solid #e2e8f0',
                borderRadius: '16px',
                fontSize: '16px',
                fontWeight: '700',
                color: '#334155',
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'border-color 0.2s',
              }}
              onMouseOver={e => e.currentTarget.style.borderColor = '#4e8bca'}
              onMouseOut={e => e.currentTarget.style.borderColor = '#e2e8f0'}
            >
              {opt.text}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderResult = () => {
    const result = getResult();
    const shareUrl = `${window.location.origin}/quiz`;

    return (
      <div style={{ padding: '20px 20px 100px' }}>
        
        {/* The capture area */}
        <div 
          ref={resultRef}
          style={{ 
            background: result.color, 
            borderRadius: '24px', 
            padding: '30px 24px', 
            color: 'white',
            textAlign: 'center',
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            marginBottom: '32px',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <div style={{ position: 'absolute', top: 20, left: 24, fontSize: '18px', fontWeight: '900', fontStyle: 'italic', opacity: 0.8 }}>
            JoinU
          </div>
          
          <div style={{ fontSize: '72px', margin: '40px 0 20px', filter: 'drop-shadow(0 10px 10px rgba(0,0,0,0.2))' }}>
            {result.emoji}
          </div>
          <div style={{ fontSize: '14px', fontWeight: '700', letterSpacing: '2px', opacity: 0.9, marginBottom: '8px' }}>
            你的社交人格是
          </div>
          <h2 style={{ fontSize: '36px', fontWeight: '900', margin: '0 0 16px', letterSpacing: '1px' }}>
            {result.title}
          </h2>
          <p style={{ fontSize: '16px', opacity: 0.95, lineHeight: '1.5', margin: '0 0 40px' }}>
            {result.desc}
          </p>

          <div style={{ background: 'white', borderRadius: '16px', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#0f172a', textAlign: 'left' }}>
            <div>
              <div style={{ fontSize: '12px', fontWeight: '800', color: '#64748b', marginBottom: '4px' }}>
                專屬推薦活動
              </div>
              <div style={{ fontSize: '18px', fontWeight: '800' }}>
                👉 揪{result.category}
              </div>
            </div>
            <div style={{ padding: '4px', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
              <QRCodeCanvas value={shareUrl} size={50} level="L" />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button 
            onClick={handleDownload}
            disabled={isGenerating}
            style={{ width: '100%', padding: '16px', background: '#0f172a', color: 'white', border: 'none', borderRadius: '16px', fontSize: '16px', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: isGenerating ? 'wait' : 'pointer' }}
          >
            {isGenerating ? '產生中...' : <><Download size={20} /> 下載結果並分享 IG 限動</>}
          </button>
          
          <button 
            onClick={() => navigate('/home')}
            style={{ width: '100%', padding: '16px', background: '#4e8bca', color: 'white', border: 'none', borderRadius: '16px', fontSize: '16px', fontWeight: '800', cursor: 'pointer' }}
          >
            去看看「揪{result.category}」活動
          </button>
          
          <button 
            onClick={() => { setCurrentStep(-1); setAnswers([]); }}
            style={{ width: '100%', padding: '16px', background: 'transparent', color: '#64748b', border: 'none', fontSize: '14px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer' }}
          >
            <RefreshCw size={16} /> 重新測驗
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <Helmet>
        <title>社交人格測驗 | JoinU 揪遊</title>
        <meta property="og:title" content="社交人格測驗 | JoinU 揪遊" />
        <meta property="og:description" content="不知道想做什麼？30秒測測看你的潛在社交人格，找出最適合你的活動！" />
      </Helmet>

      {/* Header Navigation */}
      <header style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', background: '#ffffff', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10 }}>
        <button 
          onClick={() => navigate(-1)} 
          aria-label="回上一頁"
          style={{ background: 'transparent', border: 'none', padding: 0, color: '#334155', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
          <ChevronLeft size={28} strokeWidth={2.5} />
        </button>
        <h1 style={{ margin: '0 auto', fontSize: '17px', fontWeight: '800', color: '#0f172a', paddingRight: '28px' }}>
          社交人格測驗
        </h1>
      </header>

      <div style={{ maxWidth: '480px', margin: '0 auto' }}>
        {currentStep === -1 && renderStart()}
        {currentStep >= 0 && currentStep < QUESTIONS.length && renderQuestion()}
        {currentStep === QUESTIONS.length && renderResult()}
      </div>
    </div>
  );
}
